import { useCallback, useState } from 'react';
import Helmet from 'react-helmet';
import * as S from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { getMarketTrends } from 'app/shared/flux/actions/AreaActions';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { decorate } from 'app/shared/models/TopMarkets';
import StringConstants from 'app/shared/constants/StringConstants';
import { emitAreaSearchEvent } from 'app/shared/constants/MarketTrendsCSEvents';

import ResponsiveHeading from 'app/shared/StyledComponents/ResponsiveText';
import SearchMarkets from 'app/shared/components/SearchMarkets';
import TopMarkets from 'app/shared/components/TopMarkets';
import UpsellCarouselModule from 'app/shared/modules/UpsellCarouselModule';

import type AutocompleteArea from 'app/shared/models/AutocompleteArea';
import type { State } from 'app/shared/types/State';
import { logError } from '@zg-rentals/log-error';

const MarketTrendsPage = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: State) => state.user);
  const { isMobile, screenWidth } = useSelector((state: State) => state.app.device);
  const topMarkets = useSelector((state: State) => state.topMarkets);
  const pathname = useSelector((state: State) => state.router.location.pathname);
  const queryString = useSelector((state: State) => state.router.location.search);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback((area: AutocompleteArea) => {
    async function getMarketTrendsAndNavigate() {
      const { resourceId } = area;
      if (resourceId) {
        setIsLoading(true);
        try {
          await dispatch(getMarketTrends({ resourceId }));
          emitAreaSearchEvent(user);
          setIsLoading(false);
          dispatch(push(`${pathsManifest.MarketTrendsPage}/${resourceId}/`));
        } catch (err) {
          setIsLoading(false);
          setError('An error occurred. Please try again.');
          logError({
            error: err,
            errorType: 'MarketTrends',
            context: {
              details: 'Market Stats Page: Error fetching Market Stats (CSR)',
              root: 'pages/MarketTrendsPage#submit#getMarketTrendsAndNavigate',
            },
          });
        }
      }
    }
    getMarketTrendsAndNavigate();
  }, []);

  const topRentalMarkets = topMarkets.topRentalMarkets.map((market) => decorate(market, queryString));
  const isSmallScreen = isMobile || screenWidth === 'md';

  return (
    <>
      <Helmet
        meta={[
          {
            name: 'description',
            content:
              'Use Zillow Rental Manager to compare rent across locations and see current and historical rental market data compiled by the leading real estate marketplace.',
          },
        ]}
        title="Rent Comparison Tool & Rental Market Trends Data | Zillow Rental Manager"
        link={[{ rel: 'canonical', href: `${StringConstants.ZILLOW_URL}${pathname}` }]}
      />
      <S.Container>
        <S.Content>
          <ResponsiveHeading level="2" as="h1" fontFamily="serif" marginTop="md" fontType="h2">
            Rental Market Trends
          </ResponsiveHeading>
          <SearchMarkets submit={submit} isLoading={isLoading} error={error} />
          <TopMarkets markets={topRentalMarkets} isSmallScreen={isSmallScreen} isMobile={isMobile} />
          <UpsellCarouselModule marginTop="lg" />
        </S.Content>
      </S.Container>
    </>
  );
};

export default MarketTrendsPage;
